import {
  areNeighbours,
  cellIndex,
  isInside,
  maxFactions,
  neighbours,
  type GridType,
} from './geometry';
import {
  LUMINOSITY_STEPS,
  type Board,
  type BoardDef,
  type Cell,
  type FactionDef,
  type Luminosity,
  type PlacementDef,
} from './types';

const CELL_LEGEND: Record<string, { kind: Cell['kind']; luminosity: Luminosity | null }> = {
  L: { kind: 'floor', luminosity: 'light' },
  D: { kind: 'floor', luminosity: 'dark' },
  '~': { kind: 'floor', luminosity: 'cycling' },
  '#': { kind: 'void', luminosity: null },
};

export class BoardDefError extends Error {
  constructor(message: string) {
    super(`Invalid board definition: ${message}`);
    this.name = 'BoardDefError';
  }
}

export function cellAt(board: Board, x: number, y: number): Cell | undefined {
  return isInside(board, x, y) ? board.cells[cellIndex(board, x, y)] : undefined;
}

/** Adjacent cells (four on a square board, six on a hex board) that are floor and not walled off. */
export function walkableNeighbours(board: Board, x: number, y: number): Cell[] {
  return neighbours(board, x, y).filter(
    (cell) => cell.kind === 'floor' && !hasWall(board, x, y, cell.x, cell.y),
  );
}

export function hasWall(board: Board, ax: number, ay: number, bx: number, by: number): boolean {
  const walls = board.def.walls ?? [];
  return walls.some(
    ([[x1, y1], [x2, y2]]) =>
      (x1 === ax && y1 === ay && x2 === bx && y2 === by) ||
      (x1 === bx && y1 === by && x2 === ax && y2 === ay),
  );
}

/** Validates an untrusted value (e.g. parsed JSON) and returns it typed as a `BoardDef`. */
export function validateBoardDef(input: unknown): BoardDef {
  if (!isRecord(input)) throw new BoardDefError('expected an object');
  const { id, name, grid, width, height, cells, powerPoints, walls, factions, placements } = input;
  const { luminosity } = input;

  if (typeof id !== 'string' || !/^[a-z0-9][a-z0-9-]{1,63}$/.test(id))
    throw new BoardDefError('id must be a kebab-case string');
  if (typeof name !== 'string' || name.trim().length === 0)
    throw new BoardDefError('name is required');
  if (grid !== undefined && grid !== 'square' && grid !== 'hex')
    throw new BoardDefError("grid must be 'square' or 'hex'");
  const gridType: GridType = grid ?? 'square';
  if (!isDimension(width) || !isDimension(height))
    throw new BoardDefError('width and height must be integers between 2 and 32');
  if (!Array.isArray(cells) || cells.length !== height)
    throw new BoardDefError(`cells must contain exactly ${height} row strings`);
  cells.forEach((row, y) => {
    if (typeof row !== 'string' || row.length !== width)
      throw new BoardDefError(`row ${y} must be a string of ${width} characters`);
    for (const ch of row)
      if (!(ch in CELL_LEGEND)) throw new BoardDefError(`row ${y}: unknown cell '${ch}'`);
  });

  const dims = { width, height, def: { grid: gridType } };
  if (!Array.isArray(powerPoints)) throw new BoardDefError('powerPoints must be an array');
  const seenPower = new Set<string>();
  for (const p of powerPoints) {
    if (!isCoordinate(p) || !isInside(dims, p[0], p[1]))
      throw new BoardDefError(`power point ${JSON.stringify(p)} is outside the board`);
    if (cellCharAt(cells as string[], p[0], p[1]) === '#')
      throw new BoardDefError(`power point ${JSON.stringify(p)} is on a void cell`);
    const key = `${p[0]},${p[1]}`;
    if (seenPower.has(key)) throw new BoardDefError(`duplicate power point ${key}`);
    seenPower.add(key);
  }

  if (walls !== undefined) {
    if (!Array.isArray(walls)) throw new BoardDefError('walls must be an array');
    for (const w of walls) {
      if (!Array.isArray(w) || w.length !== 2 || !isCoordinate(w[0]) || !isCoordinate(w[1]))
        throw new BoardDefError(`wall ${JSON.stringify(w)} must be two [x, y] pairs`);
      const [[x1, y1], [x2, y2]] = w;
      if (!isInside(dims, x1, y1) || !isInside(dims, x2, y2))
        throw new BoardDefError(`wall ${JSON.stringify(w)} is outside the board`);
      if (!areNeighbours(dims, { x: x1, y: y1 }, { x: x2, y: y2 }))
        throw new BoardDefError(`wall ${JSON.stringify(w)} must join adjacent cells`);
    }
  }

  if (!Array.isArray(factions) || factions.length < 2)
    throw new BoardDefError('a board needs at least two sides');
  if (factions.length > maxFactions(gridType))
    throw new BoardDefError(
      `a ${gridType} board seats at most ${maxFactions(gridType)} sides, not ${factions.length}`,
    );
  const factionIds = new Set<string>();
  for (const f of factions) {
    if (!isFaction(f)) throw new BoardDefError(`faction ${JSON.stringify(f)} is malformed`);
    if (factionIds.has(f.id)) throw new BoardDefError(`duplicate faction id '${f.id}'`);
    factionIds.add(f.id);
  }

  if (!Array.isArray(placements)) throw new BoardDefError('placements must be an array');
  const occupied = new Set<string>();
  for (const p of placements) {
    if (!isPlacement(p)) throw new BoardDefError(`placement ${JSON.stringify(p)} is malformed`);
    if (!factionIds.has(p.faction))
      throw new BoardDefError(`placement refers to unknown faction '${p.faction}'`);
    if (!isInside(dims, p.x, p.y))
      throw new BoardDefError(`placement at ${p.x},${p.y} is outside the board`);
    if (cellCharAt(cells as string[], p.x, p.y) === '#')
      throw new BoardDefError(`placement at ${p.x},${p.y} is on a void cell`);
    const key = `${p.x},${p.y}`;
    if (occupied.has(key)) throw new BoardDefError(`two pieces placed at ${key}`);
    occupied.add(key);
  }
  for (const f of factions as FactionDef[]) {
    if (!(placements as PlacementDef[]).some((p) => p.faction === f.id))
      throw new BoardDefError(`side '${f.name}' has no pieces on the board`);
  }

  if (
    luminosity !== undefined &&
    (!isRecord(luminosity) ||
      !Number.isInteger(luminosity.phase) ||
      (luminosity.phase as number) < 0 ||
      (luminosity.phase as number) >= LUMINOSITY_STEPS ||
      (luminosity.direction !== 1 && luminosity.direction !== -1))
  )
    throw new BoardDefError(
      `luminosity must name a phase 0–${LUMINOSITY_STEPS - 1} and a direction 1 or -1`,
    );

  return input as unknown as BoardDef;
}

/** Expands a (validated) definition into the cell grid used by the rest of the game. */
export function parseBoard(def: BoardDef): Board {
  const validated = validateBoardDef(def);
  const power = new Set(validated.powerPoints.map(([x, y]) => `${x},${y}`));
  const cells: Cell[] = [];
  for (let y = 0; y < validated.height; y++) {
    const row = validated.cells[y]!;
    for (let x = 0; x < validated.width; x++) {
      const legend = CELL_LEGEND[row[x]!]!;
      cells.push({
        x,
        y,
        kind: legend.kind,
        luminosity: legend.luminosity,
        powerPoint: power.has(`${x},${y}`),
      });
    }
  }
  return { def: validated, width: validated.width, height: validated.height, cells };
}

function cellCharAt(rows: string[], x: number, y: number): string {
  return rows[y]?.[x] ?? '#';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDimension(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 2 && (value as number) <= 32;
}

function isCoordinate(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    Number.isInteger(value[0]) &&
    Number.isInteger(value[1])
  );
}

function isFaction(value: unknown): value is FactionDef {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    /^[a-z0-9][a-z0-9-]{0,63}$/.test(value.id) &&
    typeof value.name === 'string' &&
    value.name.trim().length > 0 &&
    typeof value.color === 'string' &&
    /^#[0-9a-fA-F]{6}$/.test(value.color) &&
    (value.affinity === 'light' || value.affinity === 'dark' || value.affinity === 'neutral')
  );
}

function isPlacement(value: unknown): value is PlacementDef {
  return (
    isRecord(value) &&
    typeof value.faction === 'string' &&
    typeof value.character === 'string' &&
    Number.isInteger(value.x) &&
    Number.isInteger(value.y)
  );
}
